'use client';

/**
 * Y6's one interaction model: **hover reveals provenance** (VALE-1174).
 *
 * The popover states the whole change on one line (`[UPDATED] old new`), then
 * the quote answering on whose authority. One line and one quote, so it reads
 * as a sentence rather than a small form.
 *
 * **It carries a reject** — the one action Y6 gives it. Rejecting a value puts
 * the pre-AI value back, or clears it outright when the AI supplied it from
 * nothing; rejecting a contact drops it from the extract. Both write through
 * the surface's own commit path, so a reject means at apply time exactly what
 * it says on screen.
 *
 * The quote is optional: a row with no provenance still gets the popover, and
 * it simply states the change and offers the reject (VALE-1199).
 *
 * An item card may carry one more decision about the same proposal beside the
 * reject, {@link ChangePopoverProps.secondaryAction} (VALE-1887).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { ValeAIMark } from '@/components/icons/vale-ai-mark';
import { Trash2, Undo2 } from 'lucide-react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent
} from '@/components/ui/popover';
import {
  ProvenanceAttributionLine,
  ProvenanceQuote
} from '@/components/ai-review/provenance-footnote';
import { useQuoteSourceFile } from '@/components/ai-review/compact/review-source-documents';
import type { QuoteSource } from '@/lib/services/ai-analysis';
import { cn } from '@/lib/utils';
import type { FieldChangeKind } from './change-status';
import { ChangeTag, NEW_VALUE_INK, UPDATED_VALUE_INK } from './change-marks';

/**
 * The close grace.
 *
 * **Not zero, and it cannot be** — a 0ms close unmounts the popover on the
 * anchor's `mouseleave`, before the pointer can cross the few px of
 * `sideOffset` into it, so the "View source" link would never be clickable. It
 * only has to outlast that transit, which is single-digit ms. 40ms is well
 * under the ~100ms threshold at which a delay is perceived, so closing reads as
 * immediate against an open side that fires instantly. The spike measured 200ms
 * as visibly trailing the cursor and settled on 60; this trims it further, with
 * the animation now matched in both directions (VALE-1208).
 */
const HOVER_CLOSE_GRACE_MS = 40;

/** Hover-open with a close grace, shared by every anchor on one surface. */
export function useChangeHover() {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  /* A pending close must not fire into an unmounted tree. */
  useEffect(() => clear, []);

  const hoverOpen = useCallback((key: string) => {
    clear();
    setOpenKey(key);
  }, []);

  const hoverClose = useCallback(() => {
    clear();
    closeTimerRef.current = setTimeout(
      () => setOpenKey(null),
      HOVER_CLOSE_GRACE_MS
    );
  }, []);

  const closeNow = useCallback(() => {
    clear();
    setOpenKey(null);
  }, []);

  /**
   * **Close, but only if THIS key is the one open** (VALE-1264).
   *
   * The open state outlives its anchor. A row's read-out is unmounted the
   * moment the reader clicks it to edit — the grid swaps in the editor — and an
   * unmount fires no `mouseleave`, so `openKey` was left naming a row that is
   * no longer on screen. Clicking out of the editor put the read-out back, the
   * anchor re-mounted still matching, and the popover reappeared under a
   * pointer that was somewhere else entirely: the reviewer's next click landed
   * on a card they had not asked for.
   *
   * So an anchor closes itself as it goes. It cannot use {@link closeNow},
   * because by then the pointer may already have opened a DIFFERENT row's
   * popover — a `Tab` out of one field and into the next does exactly that —
   * and closing unconditionally would take that one down with it. The
   * functional update is what makes the test and the write one atomic step
   * rather than a read the next render can invalidate.
   */
  const closeIfOpen = useCallback((key: string) => {
    setOpenKey((current) => {
      if (current !== key) return current;
      clear();
      return null;
    });
  }, []);

  return { openKey, hoverOpen, hoverClose, closeNow, closeIfOpen };
}

/**
 * Where it came from, and the passage.
 *
 * **One shape, and it is the footnote's** (VALE-1417). This used to answer the
 * question its own way — a `Source for this contact` heading over the passage,
 * with the document, the page and the link run together in a muted line beneath
 * — while the Deceased's Information section a few hundred pixels up the same
 * screen answered it as a footnote. Two shapes for one question is the whole
 * defect; the footnote won because it leads with the document instead of a
 * heading restating what the reader already knows they are hovering, and gives
 * the link its own affordance rather than trailing it off a sentence.
 *
 * So this **binds** to {@link ProvenanceAttributionLine} rather than restating
 * it. Nothing about the shape is decided here, which is what stops the two
 * surfaces drifting apart a second time.
 *
 * **What the passage is FOR is unchanged.** Production carries provenance per
 * *row* — `ProvenanceFields.source` rides the diff wrapper, and a create has no
 * `FieldChange` to hang one on — so a field-level popover printing this quote
 * would claim the passage proves that one field. On a legal record that is a
 * claim worth not making, and {@link showQuote} is still how it is not made.
 */
function ChangeSourceCitation({
  source,
  sourceFileName,
  sourceFileType,
  onOpenDocument,
  showQuote = true
}: {
  source: QuoteSource | undefined;
  sourceFileName?: string;
  sourceFileType?: string;
  onOpenDocument?: (documentId: number) => void;
  /**
   * Whether to print the passage itself.
   *
   * **A FIELD's popover shows only where the value came from** (VALE-1208).
   * Provenance is per row on the wire, so the quote a field could show is the
   * whole contact's, printing it beside one field reads as evidence for that
   * field, which it is not. The file is a fact about the field's origin and
   * survives that objection; the passage stays on the CONTACT's popover, where
   * it is true.
   */
  showQuote?: boolean;
}) {
  /*
    **The surface answers, so no call site has to remember to** (VALE-1417).
    Three of the four hover cards passed a bare `source` and got the generic
    `an uploaded document` with no link, because naming the file was a job each
    call site had to opt into. It is now the review surface's, stated once, and
    an explicit prop still wins for a host that knows better than the context.
  */
  const resolved = useQuoteSourceFile(source);
  const fileName = sourceFileName ?? resolved.sourceFileName;
  const fileType = sourceFileType ?? resolved.sourceFileType;
  const openDocument = onOpenDocument ?? resolved.onOpenDocument;

  if (!source) return null;
  const quote = source.quote?.trim() ? source.quote : undefined;
  if (showQuote && !quote) return null;

  return (
    <div className="text-muted-foreground min-w-0 space-y-1 text-xs">
      {/*
        The line wraps rather than truncating. `truncate` here cut the link down
        to `View sou…` at the card's right edge — the label, the page and the
        link do not fit a `w-80` popover on one line, and the link is the part
        that pays for it. The card has the vertical room. The shared line already
        wraps (`flex-wrap`), so nothing is needed here beyond not fighting it.
      */}
      <ProvenanceAttributionLine
        /*
          The link takes its own row here. A `w-80` popover is 296px of usable
          width, and the attribution plus the link almost never fit on one line
          of it — left to wrap they broke mid-phrase and the link ended up under
          a ragged edge. The footnote's full-width band has the room and keeps
          them inline.
        */
        layout="stacked"
        source={source}
        sourceFileName={fileName}
        sourceFileType={fileType}
        /*
          The review's own document drawer, the same reader `LinkedDocumentBadge`
          opens, so a file reached from a quote and the same file reached from a
          contact's badge are one experience (VALE-1196). The page is dropped on
          the way through: this surface's opener takes the document alone.
        */
        onOpenDocument={
          openDocument ? (fileId) => openDocument(fileId) : undefined
        }
      />
      {!!quote && <ProvenanceQuote quote={quote} />}
    </div>
  );
}

export interface ChangePopoverProps {
  open: boolean;
  /**
   * The element the popover hangs off — the row, or the field's value cell.
   *
   * Rendered as the sole child of an `asChild` `PopoverAnchor` (→ Slot), which
   * requires a single valid element — `ReactNode` would let a caller pass text
   * and throw at render, since @radix-ui/react-popover 1.1.23 already resolves
   * @radix-ui/react-slot 1.3.3 (VALE-1288).
   */
  children: ReactElement;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  /** `new` puts one value in the header; `updated` puts the pair. */
  kind: Exclude<FieldChangeKind, 'unchanged'>;
  /**
   * **Whether the ROW itself draws {@link value}** (VALE-1584).
   *
   * What decides whether a new value's header line is a restatement or the only
   * place the AI's proposal is legible. A row holding a value draws it, inked,
   * right under the pointer; a row with none draws the grey `Set <label>…`
   * placeholder, and the card is then the reviewer's one sight of what they are
   * being asked to accept.
   *
   * Defaults to `false`, the safe direction: an unset caller gets the full card
   * and states the value, which is what every caller did before this existed.
   */
  valueDrawnOnRow?: boolean;
  /** What the change is now. */
  value: string;
  /** The pre-AI value. Absent on a create — there is nothing to contrast. */
  previous?: string;
  /**
   * What the change carries beyond its one-line value, printed in full under
   * the change line (VALE-1866). A proposed handover is a handler plus a note
   * and an expected-by date; run into the value line they truncated at the
   * card's edge, so the note the reviewer is judging was the part cut off.
   */
  detail?: ReactNode;
  source?: QuoteSource;
  sourceFileName?: string;
  sourceFileType?: string;
  /**
   * What an ITEM-level reject acts on, by name — "Reject this contact",
   * "Use suggested item" (VALE-1375). The citation's `Source for this <noun>`
   * heading, this prop's first job, went with VALE-1417 (the shared
   * attribution line leads with the document instead); the reject's action
   * label is what still needs the noun. Unused at field level.
   */
  itemNoun?: string;
  /** Print the passage, or only the file it came from. See the citation. */
  showQuote?: boolean;
  /**
   * Open the source document in the review's own document drawer.
   *
   * Omit and the citation states where the quote came from without offering a
   * way in.
   */
  onOpenDocument?: (documentId: number) => void;
  /** Reject this change, or, when already rejected, take it back. */
  onReject?: () => void;
  /**
   * Whether this change is already rejected.
   *
   * A reject is **reversible**, so the action has to be able to say the other
   * thing: rejecting states what will be dropped, and un-rejecting offers the
   * AI's value back. A one-way reject on a hover popover is a destructive
   * click with no undo (VALE-1208).
   */
  rejected?: boolean;
  /**
   * Whether taking the reject back puts the AI's value back (VALE-1617) — see
   * {@link rejectActionLabel}, which owns the reasoning and the wording.
   */
  restoresSuggested?: boolean;
  /**
   * **What this popover is opened from, which is what its reject acts on**
   * (VALE-1375).
   *
   * `item` is the whole row, `field` is one of its values, and the difference
   * decides both what rejecting means and what the action can honestly say it
   * will do. It replaces the `rejectLabel` / `restoreLabel` pair, which asked
   * every mount to restate wording the popover can derive: `item` plus
   * {@link itemNoun} gives "Reject this contact" and "Use suggested item"
   * without a call site typing either, and a surface cannot then drift from
   * its siblings by getting the noun or the verb wrong.
   */
  level?: 'item' | 'field';
  /**
   * **A second decision about the same proposal, beside the reject**
   * (VALE-1887).
   *
   * The reject answers "should this happen at all"; a few proposals carry a
   * second question the reviewer answers from the same evidence, and the card
   * that shows that evidence is where it belongs. The first is LF-5242's split
   * of a matched contact into a new one. Neutral ink, because neither direction
   * of it drops anything.
   */
  secondaryAction?: ChangePopoverAction;
}

/** One neutral action on a {@link ChangePopover}'s footer (VALE-1887). */
export interface ChangePopoverAction {
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}

/**
 * What the reject offers, in the two directions and at both levels.
 *
 * **Derived, never passed in.** The wording is a function of the level, the
 * change kind and the item's noun, and all three are already here. Three mounts
 * used to hand-write these four strings between them (VALE-1375).
 *
 * At field level the two kinds read differently on purpose (VALE-1208): an
 * update has a previous value, so the thing being turned down is the *change*;
 * a create or an enrichment has none, so it is the *value*.
 */
export function rejectActionLabel({
  level,
  kind,
  itemNoun,
  rejected,
  restoresSuggested = true
}: {
  level: 'item' | 'field';
  kind: Exclude<FieldChangeKind, 'unchanged'>;
  itemNoun: string;
  rejected: boolean;
  /**
   * **Whether taking the reject back actually puts the AI's value back**
   * (VALE-1617).
   *
   * Un-rejecting used to say `Use suggested value` unconditionally, and on one
   * path that was a lie the reviewer could act on. A reject does not touch the
   * buffer — it is applied at commit, by clearing the key — so taking it back
   * only stops the clearing, and whatever the buffer holds is what applies. If
   * the reviewer never typed over the AI's value, the buffer still holds it and
   * the label is true. If they DID, the buffer holds their own edit: the button
   * offered the AI's suggestion and reinstated the reviewer's, which is the
   * confusion this exists to remove.
   *
   * The treatment restores the suggestion for a plain scalar row and passes
   * `false` here for the row kinds whose value it cannot rebuild from a diff
   * value alone — a picker resolving a relation, a flag with its own draft
   * shape, a composed money row committing parts. Those keep the honest
   * wording instead of a promise the click will not keep.
   *
   * Defaults to `true`, which is the pre-VALE-1617 wording, so a caller that
   * has not thought about it reads exactly as it did.
   */
  restoresSuggested?: boolean;
}): string {
  if (level === 'item') {
    return rejected ? `Use suggested ${itemNoun}` : `Reject this ${itemNoun}`;
  }
  if (rejected)
    return restoresSuggested ? 'Use suggested value' : 'Undo reject';
  if (kind === 'added') return 'Reject this addition';
  return kind === 'updated' ? 'Reject this change' : 'Reject this value';
}

export function ChangePopover({
  open,
  children,
  onMouseEnter,
  onMouseLeave,
  kind,
  value,
  valueDrawnOnRow = false,
  previous,
  detail,
  source,
  sourceFileName,
  sourceFileType,
  showQuote = true,
  onOpenDocument,
  onReject,
  rejected = false,
  restoresSuggested = true,
  itemNoun = 'item',
  level = 'field',
  secondaryAction
}: ChangePopoverProps) {
  const rejectActionText = rejectActionLabel({
    level,
    kind,
    itemNoun,
    rejected,
    restoresSuggested
  });

  /**
   * **A NEW value's field popover is the reject and nothing else** (VALE-1584).
   *
   * The full card states the change on one line, and for an `updated` field
   * that line is the whole point: it is the only place the superseded value is
   * legible beside the one replacing it. A `new` field has no superseded value,
   * so the same line restates the value the reader is already looking at, inked
   * — a `w-80` card that answers a question nobody asked, opening on hover over
   * most of a freshly-extracted record.
   *
   * It is not dropped outright, which is what this ticket first proposed,
   * because {@link rowHoldsAValue} already records why that was tried and
   * rejected: the popover is the only place a new value's reject lives on a
   * `text` or `number` row, since `offersClear` withholds the `×` from exactly
   * those kinds (VALE-129). So the card shrinks to the one thing on it that is
   * not a restatement, and the reject survives on every row that has one.
   *
   * **Only where the row actually draws the value**
   * ({@link valueDrawnOnRow}) — the header line is a restatement only when
   * there is something to restate. A row with no value draws the grey
   * `Set <label>…` placeholder, and VALE-1264 put the PROPOSED value in this
   * header precisely because the card is then the reviewer's only sight of it;
   * shrinking that away would leave them rejecting a value they were never
   * shown. `ai-review-field-marking.test.tsx` holds that case.
   *
   * **Field level only.** An ITEM's popover carries the provenance quote and
   * the passage it came from, which is the one place that evidence is true —
   * a new contact's card is not a restatement of anything and keeps its shape.
   */
  /*
    An ADDITION's field card is the reject alone too (VALE-1951): the row
    already draws the recorded note and the addition after it, so the header
    line would only restate them.
  */
  const minimal =
    level === 'field' &&
    (kind === 'new' || kind === 'added') &&
    valueDrawnOnRow;

  /*
    Minimal with no reject leaves nothing to draw, which is the ticket's
    original rule arriving at its correct scope: a surface that offers no reject
    gives a new value its ink and nothing else. The anchor still renders — it is
    a Slot, so suppressing here costs no DOM — and only the card is withheld.
  */
  const suppressed = minimal && !onReject;

  return (
    <Popover open={open && !suppressed} onOpenChange={() => undefined}>
      <PopoverAnchor asChild>{children}</PopoverAnchor>
      <PopoverContent
        align="start"
        side="bottom"
        /*
          **In and out take the same time, and both are quicker than the
          default** (VALE-1208). `PopoverContent` inherits a 150ms enter/exit,
          and because the exit only starts after the close grace below, out felt
          markedly slower than in. `duration-100` on both states makes the two
          directions match and takes the edge off each.
        */
        className={cn(
          'duration-100 data-[state=closed]:duration-100 data-[state=open]:duration-100',
          /*
            Sized to the reject rather than to a value line it no longer draws.
            `w-auto` on a card whose only child is one short button is a chip,
            which is the weight this treatment is meant to carry.
          */
          minimal ? 'w-auto p-2' : 'w-80 p-3'
        )}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={cn('flex flex-col', !minimal && 'gap-2.5')}>
          {/*
            The change on one line. An update puts two values inside a `w-80`
            popover, so the OLD value gives way first (`shrink-[100]`) — the new
            value is the one the reviewer is being asked about.

            Withheld on a minimal card, where it would restate the inked value
            the reader is hovering — see {@link minimal}.
          */}
          {!minimal && (
            <div className="flex min-w-0 items-center gap-2">
              <ChangeTag kind={kind} />
              <span className="flex min-w-0 items-center gap-1.5 text-xs">
                {!!previous && (
                  <span className="text-muted-foreground min-w-0 shrink-[100] truncate line-through decoration-1">
                    {previous}
                  </span>
                )}
                <span
                  className={cn(
                    'min-w-0 truncate font-medium',
                    previous ? UPDATED_VALUE_INK : NEW_VALUE_INK
                  )}
                >
                  {value}
                </span>
              </span>
            </div>
          )}

          {!minimal && !!detail && (
            <div className="text-foreground min-w-0 space-y-1 text-xs break-words">
              {detail}
            </div>
          )}

          {/*
            Already `null` at field level — the treatment passes no `source`
            (VALE-1208) — so this is the ITEM card's, and the guard only spares
            it the call.
          */}
          {!minimal && (
            <ChangeSourceCitation
              source={source}
              sourceFileName={sourceFileName}
              sourceFileType={sourceFileType}
              onOpenDocument={onOpenDocument}
              showQuote={showQuote}
            />
          )}

          {(!!onReject || !!secondaryAction) && (
            /* No rule above the action when the action is all there is. */
            <div
              className={cn(
                'flex flex-wrap items-center gap-x-4 gap-y-1',
                !minimal && 'border-border border-t pt-2'
              )}
            >
              {/*
                The reject reads as a reject — `error` ink and a glyph naming
                what it does. An UPDATE has a previous value to put back (the
                back arrow); a create or an enrichment has nothing to fall back
                to, so its reject removes (the bin).
              */}
              {/*
                The undo of a reject stays NEUTRAL, putting the AI's value
                back is not destructive, and colouring it red would say it was.
              */}
              {!!onReject && (
                <button
                  type="button"
                  className={cn(
                    'inline-flex cursor-pointer items-center gap-1 text-xs font-medium underline-offset-2 hover:underline',
                    rejected
                      ? 'text-muted-foreground hover:text-foreground'
                      : 'text-error-subtle-foreground hover:text-error-subtle-foreground'
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    onReject();
                  }}
                >
                  {/*
                  **The glyph follows the change kind, not the presence of a
                  `previous` string** (VALE-1375). The two agree at field level,
                  where an update always carries one — but an ITEM's popover
                  passes no `previous` at all (there is no single value to
                  contrast), so an updated contact or asset drew the bin and
                  said its reject would remove something, when rejecting an
                  update leaves the existing record exactly as it was.
                */}
                  {rejected ? (
                    /*
                    **Vale's own mark on the value Vale suggested** (VALE-1617).

                    Taking a reject back puts the AI's value back, and the
                    purple mark is how this product says *a machine produced
                    this* everywhere else — the review panel, the provenance
                    footnote, the AI card's chip. The action that hands one of
                    those values back should carry it too.

                    **Gated on `restoresSuggested`, not on `rejected` alone.**
                    Where the treatment cannot rebuild the AI's value the label
                    reads `Undo reject` and the click restores nothing, so a
                    mark there would attribute to Vale an outcome it is not
                    about to produce. An item the reviewer added themselves
                    never reaches this at all: it carries no change status, so
                    the treatment is the identity function and there is no
                    popover on it.
                  */
                    restoresSuggested ? (
                      <ValeAIMark box={14} className="text-ai shrink-0" />
                    ) : (
                      <Undo2 className="size-3.5 shrink-0" aria-hidden />
                    )
                  ) : kind === 'updated' || kind === 'added' ? (
                    /* An addition rejected leaves the recorded note exactly as it was. */
                    <Undo2 className="size-3.5 shrink-0" aria-hidden />
                  ) : (
                    <Trash2 className="size-3.5 shrink-0" aria-hidden />
                  )}
                  {rejectActionText}
                </button>
              )}
              {!!secondaryAction && (
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground inline-flex cursor-pointer items-center gap-1 text-xs font-medium underline-offset-2 hover:underline"
                  onClick={(event) => {
                    event.stopPropagation();
                    secondaryAction.onSelect();
                  }}
                >
                  {secondaryAction.icon}
                  {secondaryAction.label}
                </button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
